import React from "react";
import {
  FaHandsHelping,
  FaUsers,
  FaLifeRing,
  FaChartPie,
} from "react-icons/fa";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";
import { Link } from "react-router-dom";

const ServiceSection = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= SEO TOOL SERVICES DATA ================= */
  const services = [
    {
      id: 1,
      icon: (
        <FaHandsHelping
          size={34}
          className="text-[#008300]"
          aria-hidden="true"
        />
      ),
      title: "AI-Powered SEO Automation",
      description:
        "Automate complex SEO tasks using artificial intelligence that analyzes more than 50 ranking metrics and fixes website SEO issues instantly.",
    },
    {
      id: 2,
      icon: <FaUsers size={34} className="text-[#008300]" aria-hidden="true" />,
      title: "Built for All Businesses",
      description:
        "Ideal for bloggers, startups, marketers, agencies, and businesses managing multiple WordPress websites and SEO campaigns.",
    },
    {
      id: 3,
      icon: (
        <FaLifeRing size={34} className="text-[#008300]" aria-hidden="true" />
      ),
      title: "Safe & Reliable Optimization",
      description:
        "Every SEO change includes automatic backup and rollback protection so your website remains secure during optimization.",
    },
    {
      id: 4,
      icon: (
        <FaChartPie size={34} className="text-[#008300]" aria-hidden="true" />
      ),
      title: "Real-Time SEO Insights",
      description:
        "Monitor keyword rankings, website performance, and SEO analytics with actionable insights for faster organic growth.",
    },
  ];

  /* ================= THEME ================= */
  const sectionBg = isDarkMode ? "bg-gray-950" : "bg-white";
  const headingText = isDarkMode ? "text-white" : "text-black";
  const paraText = isDarkMode ? "text-gray-400" : "text-gray-600";

  const cardBg = isDarkMode
    ? "bg-gray-900 text-white border border-gray-800"
    : "bg-white text-black shadow-lg";

  const circleColor = isDarkMode ? "bg-[#008300]/15" : "bg-[#008300]/10";

  return (
    <section
      aria-label="Seo Up Rank AI SEO tool features and services"
      className={`relative py-24 overflow-hidden ${sectionBg}`}
    >

      {/* ================= STRUCTURED DATA ================= */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Seo Up Rank",
            applicationCategory: "SEO Tool",
            operatingSystem: "Web",
            description:
              "AI powered SEO automation platform that analyzes ranking factors, performs SEO audits, and optimizes websites automatically.",
            url: "https://novarsis.ai/",
          }),
        }}
      />

      {/* ================= HIDDEN SEO CONTEXT ================= */}
      <p className="sr-only">
        Seo Up Rank is an AI powered SEO automation tool designed to automate
        WordPress SEO optimization, improve website rankings, analyze search
        engine performance, and increase organic traffic using advanced AI
        technology.
      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}
      <p className="sr-only">
        AI SEO tool, automated SEO audit platform, WordPress SEO automation,
        keyword ranking optimization tool, technical SEO analysis software,
        SEO analytics dashboard, AI SEO optimization platform.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}
      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* Decorative Circles */}
      <div
        aria-hidden="true"
        className={`absolute -top-24 -left-24 w-72 h-72 ${circleColor} rounded-full`}
      />
      <div
        aria-hidden="true"
        className={`absolute top-40 right-0 w-64 h-64 ${circleColor} rounded-full`}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* ================= HEADER ================= */}
        <header className="text-center mb-16 max-w-3xl mx-auto">

          <Reveal>
            <h2
              className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 ${headingText}`}
            >
              Why Choose Our <span className="text-[#008300]">AI SEO Tool</span>
            </h2>
          </Reveal>

          {/* Extra hidden SEO heading */}
          <h2 className="sr-only">
            AI powered SEO automation platform for website optimization
          </h2>

          <Reveal>
            <p className={`text-base sm:text-lg ${paraText}`}>
              <strong>Seo Up Rank</strong> helps businesses automate WordPress
              SEO, improve website rankings, and increase organic traffic using
              advanced AI-powered optimization tools.
            </p>
          </Reveal>

          {/* Internal SEO Link */}
          <div className="sr-only">
            <Link
              to="/pricing"
              aria-label="View Seo Up Rank pricing plans"
              className="inline-block mt-6 text-[#008300] font-semibold hover:underline"
            >
              Explore SEO Pricing →
            </Link>
          </div>

        </header>

        {/* ================= SERVICES GRID ================= */}
        <div
          role="list"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10"
        >
          {services.map((service) => (
            <article
              key={service.id}
              role="listitem"
              className={`group relative rounded-2xl p-8 text-center
              transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl
              ${cardBg}`}
            >

              {/* Card Circle Accent */}
              <div
                aria-hidden="true"
                className={`absolute -top-10 left-1/2 -translate-x-1/2
                w-24 h-24 ${circleColor} rounded-full`}
              />

              {/* Icon */}
              <div
                className={`relative mx-auto mb-6 w-20 h-20 rounded-full
                flex items-center justify-center shadow-md
                ${isDarkMode ? "bg-gray-800" : "bg-white"}
                group-hover:bg-[#008300]/10 transition`}
              >
                {service.icon}
              </div>

              <Reveal>
                <h3 className="text-lg font-bold mb-3">{service.title}</h3>
              </Reveal>

              <Reveal>
                <p
                  className={`text-sm leading-relaxed text-justify ${paraText}`}
                >
                  {service.description}
                </p>
              </Reveal>

            </article>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ServiceSection;